import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Bot, Users, BarChart3 } from "lucide-react";
import SEO from "@/components/site/SEO";
import SectionHeading from "@/components/site/SectionHeading";
import FinalCTA from "@/components/site/FinalCTA";
import CapabilityDiagram from "@/components/site/CapabilityDiagram";
import { CAPABILITIES } from "@/data/content";

const iconMap = {
    "creative-systems": Sparkles,
    "ai-agent-workflows": Bot,
    "human-talent": Users,
    "growth-engines": BarChart3,
};

export default function CapabilitiesPage({ onStartProject }) {
    return (
        <div data-testid="page-capabilities">
            <SEO
                title="Capabilities — AI Agent Workflows, Creative Systems & Growth Engines"
                description="Explore IdeaClap's four core capabilities: Creative Systems, AI Agent Workflows, Human Talent Network, and Growth Engines. Plain-English diagrams of how AI + human judgement compound."
                path="/capabilities"
                keywords="AI agent workflows, creative systems, growth engines, AI marketing capabilities, human talent network, agentic operations"
            />
            <header className="relative pt-32 lg:pt-40 pb-20 overflow-hidden">
                <div className="absolute inset-0 hero-radial pointer-events-none" />
                <div className="absolute inset-0 dot-grid-fine opacity-50 pointer-events-none [mask-image:radial-gradient(ellipse_at_top,#000_30%,transparent_75%)]" />
                <div className="relative mx-auto max-w-7xl container-px">
                    <SectionHeading
                        eyebrow="Capabilities"
                        title={
                            <>
                                A platform of capabilities,
                                <br className="hidden md:block" />
                                <span className="italic text-[hsl(var(--text-muted))]">not a list of services.</span>
                            </>
                        }
                        subtitle="Each capability is a system designed to compound — brand, agentic workflows, curated talent, and growth engines."
                    />
                </div>
            </header>

            <section className="mx-auto max-w-7xl container-px pb-24 lg:pb-28">
                <div className="flex flex-col gap-16 lg:gap-24">
                    {CAPABILITIES.map((c, i) => {
                        const Icon = iconMap[c.id] || Sparkles;
                        const reverse = i % 2 === 1;
                        return (
                            <article
                                key={c.id}
                                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
                                id={c.id}
                            >
                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-10% 0px" }}
                                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                                    className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}
                                >
                                    <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                        {c.eyebrow}
                                    </span>
                                    <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-[60px] leading-[1.02] tracking-[-0.02em] text-[hsl(var(--text))]">
                                        {c.title}
                                    </h2>
                                    <p className="mt-5 text-[16px] leading-relaxed text-[hsl(var(--text-muted))] max-w-[60ch]">
                                        {c.description}
                                    </p>
                                    <ul className="mt-7 space-y-3">
                                        {c.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-3 text-[15px] text-[hsl(var(--text-muted))]"
                                            >
                                                <span
                                                    className={`mt-2 h-1.5 w-1.5 rounded-full ${
                                                        c.accent === "gold"
                                                            ? "bg-[hsl(var(--gold))]"
                                                            : "bg-[hsl(var(--blue))]"
                                                    }`}
                                                />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-10% 0px" }}
                                    transition={{ duration: 0.6, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                                    className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}
                                >
                                    <div className="relative aspect-square rounded-3xl border border-black/[0.08] bg-white p-6 overflow-hidden shadow-soft">
                                        <div className="absolute inset-0 dot-grid-fine opacity-50 pointer-events-none" />
                                        <div className="absolute -top-10 -right-10 h-48 w-48 rounded-full bg-[rgba(37,99,235,0.10)] blur-3xl pointer-events-none" />
                                        <div className="absolute -bottom-12 -left-10 h-56 w-56 rounded-full bg-[rgba(187,118,33,0.08)] blur-3xl pointer-events-none" />
                                        <div className="relative z-10 h-full w-full flex flex-col">
                                            <div className="flex items-center justify-between">
                                                <span
                                                    className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ${
                                                        c.accent === "gold"
                                                            ? "bg-[rgba(187,118,33,0.10)] ring-[rgba(187,118,33,0.35)] text-[hsl(var(--gold))]"
                                                            : "bg-[rgba(37,99,235,0.10)] ring-[rgba(37,99,235,0.35)] text-[hsl(var(--blue))]"
                                                    }`}
                                                >
                                                    <Icon className="h-5 w-5" />
                                                </span>
                                                <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                                    System diagram
                                                </span>
                                            </div>
                                            <div className="mt-7 flex-1 min-h-0">
                                                <CapabilityDiagram id={c.id} />
                                            </div>
                                            <div className="mt-6 grid grid-cols-2 gap-3">
                                                <div className="rounded-xl border border-black/[0.08] bg-[hsl(var(--bg-2))] p-3">
                                                    <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                                        Cycle time
                                                    </div>
                                                    <div className="font-display text-2xl text-[hsl(var(--text))] mt-1">
                                                        −90%
                                                    </div>
                                                </div>
                                                <div className="rounded-xl border border-black/[0.08] bg-[hsl(var(--bg-2))] p-3">
                                                    <div className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                                        Quality
                                                    </div>
                                                    <div className="font-display text-2xl text-[hsl(var(--text))] mt-1">
                                                        +Senior
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </article>
                        );
                    })}
                </div>
            </section>

            <FinalCTA onStartProject={onStartProject} />
        </div>
    );
}
